// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

// DECISION: Supports both legacy Coinbase API keys (HMAC-SHA256) and
// Coinbase Developer Platform (CDP) keys (ES256 JWT with EC private key).
// Key type is auto-detected by name format: CDP keys start with "organizations/".

// DECISION: Auth uses HMAC-SHA256 for legacy keys (CB-ACCESS-* headers) and
// ES256 JWT for CDP keys (sent as X-Coinbase-JWT; proxy converts to Authorization).
// In both cases the secret/private key never leaves the browser.

// DECISION: All Coinbase API calls route through our own sync-server proxy to
// avoid browser CORS restrictions. The signature is still computed client-side.
// In Docker (VITE_API_URL=/api): /api/coinbase-proxy/... → nginx strips /api → sync server
// In Vite dev (VITE_API_URL unset): /coinbase-proxy/... → Vite proxy → sync server

const COINBASE_PROXY_BASE =
  ((import.meta.env['VITE_API_URL'] as string | undefined) ?? '') + '/coinbase-proxy';
const CB_VERSION = '2016-02-18';
const COINBASE_API_HOST = 'api.coinbase.com';

export interface CoinbaseApiCredential {
  apiKey: string;     // Legacy: short key ID; CDP: "organizations/…/apiKeys/…"
  apiSecret: string;  // Legacy: HMAC secret; CDP: PEM-encoded EC private key
  jwtToken: string;   // Hushold app JWT (for our proxy's auth)
}

function isCdpKey(apiKey: string): boolean {
  return apiKey.startsWith('organizations/');
}

// ─── DER / PKCS#8 helpers ─────────────────────────────────────────────────
// SubtleCrypto only accepts EC private keys in PKCS#8 format. Coinbase CDP
// keys are often in SEC1 format (-----BEGIN EC PRIVATE KEY-----), so we wrap
// them in the standard PKCS#8 envelope before importing.

function pemToDer(pem: string): Uint8Array {
  // Normalize JSON-escaped newlines (\n as literal backslash-n from a JSON file copy-paste)
  const normalized = pem.replace(/\\n/g, '\n');
  // Extract only the PEM block — skips any prefix (e.g. org ID glued before -----BEGIN)
  const match = normalized.match(/-----BEGIN [^-]+-----[\s\S]*?-----END [^-]+-----/);
  if (!match) throw new Error('No PEM block found in the provided key');
  const b64 = match[0].replace(/-----[^-]+-----/g, '').replace(/\s/g, '');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function concat(...bufs: Uint8Array[]): Uint8Array {
  const total = bufs.reduce((s, b) => s + b.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const b of bufs) { result.set(b, offset); offset += b.length; }
  return result;
}

function derLen(n: number): Uint8Array {
  if (n < 0x80) return new Uint8Array([n]);
  if (n < 0x100) return new Uint8Array([0x81, n]);
  return new Uint8Array([0x82, n >> 8, n & 0xff]);
}

function asn1Seq(inner: Uint8Array): Uint8Array {
  return concat(new Uint8Array([0x30]), derLen(inner.length), inner);
}

function asn1OctetStr(data: Uint8Array): Uint8Array {
  return concat(new Uint8Array([0x04]), derLen(data.length), data);
}

// AlgorithmIdentifier: id-ecPublicKey (OID) + prime256v1 (OID)
const EC_P256_ALG_ID = new Uint8Array([
  0x30, 0x13,
  0x06, 0x07, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x02, 0x01,
  0x06, 0x08, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x03, 0x01, 0x07,
]);
const DER_INTEGER_0 = new Uint8Array([0x02, 0x01, 0x00]);

// Wraps a SEC1 (-----BEGIN EC PRIVATE KEY-----) DER blob in a PKCS#8 envelope.
function sec1ToPkcs8(sec1Der: Uint8Array): Uint8Array {
  return asn1Seq(concat(DER_INTEGER_0, EC_P256_ALG_ID, asn1OctetStr(sec1Der)));
}

async function importEcPrivateKey(pem: string): Promise<CryptoKey> {
  const der = pemToDer(pem);
  const isPkcs8 = pem.includes('-----BEGIN PRIVATE KEY-----');
  const raw = isPkcs8 ? der : sec1ToPkcs8(der);
  // Copy into a fresh ArrayBuffer; SubtleCrypto requires ArrayBuffer, not ArrayBufferLike.
  const pkcs8 = new ArrayBuffer(raw.length);
  new Uint8Array(pkcs8).set(raw);
  return crypto.subtle.importKey(
    'pkcs8',
    pkcs8,
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign'],
  );
}

// ─── CDP ES256 JWT signing ────────────────────────────────────────────────

function b64url(data: ArrayBuffer | string): string {
  const bytes = typeof data === 'string'
    ? new TextEncoder().encode(data)
    : new Uint8Array(data);
  let bin = '';
  for (const byte of bytes) bin += String.fromCharCode(byte);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// Creates a short-lived JWT signed with the CDP private key, suitable for
// authenticating a single request to api.coinbase.com.
async function signCdpJwt(
  keyName: string,
  privateKeyPem: string,
  method: string,
  path: string,
): Promise<string> {
  const key = await importEcPrivateKey(privateKeyPem);
  const now = Math.floor(Date.now() / 1000);
  const nonceBytes = new Uint8Array(16);
  crypto.getRandomValues(nonceBytes);
  const nonce = Array.from(nonceBytes).map(b => b.toString(16).padStart(2, '0')).join('');
  const header = { alg: 'ES256', kid: keyName, nonce };
  const payload = {
    sub: keyName,
    iss: 'cdp',
    nbf: now,
    exp: now + 120,
    // Strip query params — Coinbase matches the uri claim against the path only.
    uri: `${method.toUpperCase()} ${COINBASE_API_HOST}/v2${path.split('?')[0]}`,
  };
  const sigInput = `${b64url(JSON.stringify(header))}.${b64url(JSON.stringify(payload))}`;
  // SubtleCrypto ECDSA returns raw R||S (IEEE P1363), which is what JWT ES256 expects.
  const sig = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    key,
    new TextEncoder().encode(sigInput),
  );
  return `${sigInput}.${b64url(sig)}`;
}

// ─── Legacy HMAC-SHA256 signing ───────────────────────────────────────────

async function coinbaseApiKeyHeaders(
  credential: CoinbaseApiCredential,
  method: string,
  path: string,
  body = '',
): Promise<HeadersInit> {
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const message = timestamp + method.toUpperCase() + '/v2' + path + body;
  const keyData = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(credential.apiSecret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', keyData, new TextEncoder().encode(message));
  const sigHex = Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return {
    'CB-ACCESS-KEY': credential.apiKey,
    'CB-ACCESS-SIGN': sigHex,
    'CB-ACCESS-TIMESTAMP': timestamp,
    'CB-VERSION': CB_VERSION,
    'Content-Type': 'application/json',
  };
}

// ─── Proxy fetch ──────────────────────────────────────────────────────────

async function coinbaseFetch<T>(
  credential: CoinbaseApiCredential,
  path: string,
): Promise<T> {
  let authHeaders: HeadersInit;

  if (isCdpKey(credential.apiKey)) {
    // CDP key: create an ES256 JWT client-side, forward via X-Coinbase-JWT.
    // The proxy converts this to Authorization: Bearer for Coinbase.
    const cdpJwt = await signCdpJwt(credential.apiKey, credential.apiSecret, 'GET', path);
    authHeaders = {
      'X-Coinbase-JWT': cdpJwt,
      'CB-VERSION': CB_VERSION,
      'Content-Type': 'application/json',
    };
  } else {
    // Legacy key: HMAC-SHA256 signed headers (CB-ACCESS-KEY/SIGN/TIMESTAMP).
    authHeaders = await coinbaseApiKeyHeaders(credential, 'GET', path);
  }

  const response = await fetch(`${COINBASE_PROXY_BASE}${path}`, {
    headers: { ...authHeaders, Authorization: `Bearer ${credential.jwtToken}` },
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Coinbase API error (${response.status}) at ${path}: ${text}`);
  }
  return response.json() as Promise<T>;
}

// ─── Accounts ────────────────────────────────────────────────────────────────

export interface CoinbaseAccount {
  id: string;
  name: string;
  type: 'wallet' | 'vault' | 'fiat';
  currency: {
    code: string;
    name: string;
  };
  balance: {
    amount: string;
    currency: string;
  };
}

interface PaginatedResponse<T> {
  data: T[];
  pagination?: {
    next_uri: string | null;
  };
}

export async function fetchCoinbaseAccounts(
  credential: CoinbaseApiCredential,
): Promise<CoinbaseAccount[]> {
  const results: CoinbaseAccount[] = [];
  let nextPath: string | null = '/accounts?limit=100';

  while (nextPath !== null) {
    const path: string = nextPath;
    const page: PaginatedResponse<CoinbaseAccount> = await coinbaseFetch<PaginatedResponse<CoinbaseAccount>>(
      credential,
      path,
    );
    results.push(...page.data);
    const nextUri = page.pagination?.next_uri ?? null;
    // next_uri includes the /v2 prefix — strip it to get just the path
    nextPath = nextUri ? nextUri.replace(/^\/v2/, '') : null;
  }

  return results;
}

// ─── Transactions ─────────────────────────────────────────────────────────────

export interface CoinbaseTransaction {
  id: string;
  type:
    | 'send'
    | 'receive'
    | 'buy'
    | 'sell'
    | 'fiat_deposit'
    | 'fiat_withdrawal'
    | 'exchange_deposit'
    | 'exchange_withdrawal'
    | 'vault_withdrawal'
    | 'interest'
    | 'staking_reward'
    | 'inflation_reward';
  status: 'pending' | 'completed' | 'failed' | 'expired' | 'canceled';
  amount: { amount: string; currency: string };
  native_amount: { amount: string; currency: string };
  description: string | null;
  created_at: string;
  updated_at: string;
  resource_path: string;
  details: { title: string; subtitle: string };
  buy?: { id: string; resource: string };
  sell?: { id: string; resource: string };
  fiat_deposit?: { id: string };
  fiat_withdrawal?: { id: string };
}

export async function fetchCoinbaseTransactions(
  credential: CoinbaseApiCredential,
  accountId: string,
  since?: string,
): Promise<CoinbaseTransaction[]> {
  const results: CoinbaseTransaction[] = [];
  let nextPath: string | null = `/accounts/${accountId}/transactions?limit=100&expand[]=buy&expand[]=sell`;

  while (nextPath !== null) {
    const path: string = nextPath;
    const page: PaginatedResponse<CoinbaseTransaction> = await coinbaseFetch<PaginatedResponse<CoinbaseTransaction>>(
      credential,
      path,
    );
    let hitCutoff = false;
    for (const tx of page.data) {
      if (since !== undefined && tx.created_at <= since) { hitCutoff = true; break; }
      results.push(tx);
    }
    if (hitCutoff) break;
    if (since !== undefined && page.data.length > 0) {
      const last = page.data[page.data.length - 1];
      if (last !== undefined && last.created_at <= since) break;
    }
    const nextUri = page.pagination?.next_uri ?? null;
    nextPath = nextUri ? nextUri.replace(/^\/v2/, '') : null;
  }

  return results;
}

// ─── Buy / Sell Details ───────────────────────────────────────────────────────

export interface CoinbaseBuySellDetails {
  amount: { amount: string; currency: string };
  total: { amount: string; currency: string };
  subtotal: { amount: string; currency: string };
  fees: Array<{ amount: { amount: string; currency: string }; type: string }>;
  unit_price: { amount: string; currency: string };
}

export async function fetchCoinbaseBuyDetails(
  credential: CoinbaseApiCredential,
  accountId: string,
  buyId: string,
): Promise<CoinbaseBuySellDetails> {
  const resp = await coinbaseFetch<{ data: CoinbaseBuySellDetails }>(
    credential,
    `/accounts/${accountId}/buys/${buyId}`,
  );
  return resp.data;
}

export async function fetchCoinbaseSellDetails(
  credential: CoinbaseApiCredential,
  accountId: string,
  sellId: string,
): Promise<CoinbaseBuySellDetails> {
  const resp = await coinbaseFetch<{ data: CoinbaseBuySellDetails }>(
    credential,
    `/accounts/${accountId}/sells/${sellId}`,
  );
  return resp.data;
}
